import type { RiskLevel } from '../api/types';

/**
 * Risk badge — used everywhere risk levels appear.
 *
 * showScore renders the engine's normalized_score on its native 0–100 scale,
 * to one decimal place. Do NOT rescale it here: the same value is displayed
 * raw elsewhere in the same views (results.tsx module rows and its CSV
 * export, asset-grid columns), so dividing by 10 made one score appear two
 * different ways on a single screen. If a 0–10 presentation is wanted, it has
 * to change in the engine's threshold bands and every display site together,
 * not in this component alone.
 */
const PALETTE: Record<RiskLevel, { bg: string; fg: string; label: string }> = {
  No_risk:   { bg: 'bg-risk-no_risk/10',   fg: 'text-risk-no_risk',   label: 'No risk' },
  Very_low:  { bg: 'bg-risk-very_low/10',  fg: 'text-risk-very_low',  label: 'Very low' },
  Low:       { bg: 'bg-risk-low/10',       fg: 'text-risk-low',       label: 'Low' },
  Moderate:  { bg: 'bg-risk-moderate/10',  fg: 'text-risk-moderate',  label: 'Moderate' },
  High:      { bg: 'bg-risk-high/10',      fg: 'text-risk-high',      label: 'High' },
  Very_high: { bg: 'bg-risk-very_high/10', fg: 'text-risk-very_high', label: 'Very high' },
  Critical:  { bg: 'bg-risk-critical/10',  fg: 'text-risk-critical',  label: 'Critical' },
  NA:        { bg: 'bg-risk-na/10',        fg: 'text-risk-na',        label: 'N/A' },
};

interface Props {
  level: RiskLevel | null;
  size?: 'xs' | 'sm';
  /** Engine normalized_score (0–100). Displayed as-is to one decimal. */
  showScore?: number | null;
}

export function RiskBadge({ level, size = 'xs', showScore }: Props) {
  if (level === null) {
    return (
      <span className={size === 'xs'
        ? 'text-ink-300 text-xs font-mono tabular-nums'
        : 'text-ink-300 text-sm'}>
        —
      </span>
    );
  }
  const p = PALETTE[level];
  const sizeClasses = size === 'xs'
    ? 'px-1.5 py-0.5 text-[10px] tracking-wide'
    : 'px-2 py-1 text-xs tracking-wide';

  const displayScore = showScore != null && level !== 'NA'
    ? showScore.toFixed(1)
    : null;

  return (
    <span className={`inline-flex items-center gap-1.5 ${sizeClasses} ${p.bg} ${p.fg} font-semibold uppercase rounded-sm`}>
      <span>{p.label}</span>
      {displayScore != null && (
        <span className="font-mono tabular-nums opacity-70">
          {displayScore}
        </span>
      )}
    </span>
  );
}